<template>
  <slot name="sidebar" />
  <!-- The page card floats 6px clear of the window, but not of the sidebar, which has its own padding. -->
  <div
    id="app-main-content"
    ref="mainContentRef"
    class="flex-1 flex flex-col gap-4 rounded-xl h-full p-1.5 lg:pl-0 define-width-0-100-100"
  >
    <!-- `--page-gutter` is this area's padding, which full-width rows cancel; the bottom grows only under a floating bar. -->
    <div
      id="app-main-content-area"
      ref="mainContentAreaRef"
      class="rounded-2xl bg-page [--page-sticky-top:calc(3.5rem-var(--page-gutter))] relative flex-1 flex flex-col gap-4 p-[var(--page-gutter)] [body:has([data-floating-bar])_&]:pb-[6rem] [--page-gutter:0.75rem] sm:[--page-gutter:1rem] xl:[--page-gutter:1.5rem] overflow-auto define-width-0-100-100"
    >
      <!-- The page header: breadcrumb plus teleported actions, one height with or without them; its line spans the page. -->
      <div
        class="-mx-[var(--page-gutter)] -mt-[var(--page-gutter)] sticky -top-[var(--page-gutter)] z-20 flex min-h-14 shrink-0 flex-wrap items-center gap-2 border-b border-ground bg-page lg:h-14 lg:flex-nowrap px-[var(--page-gutter)] py-2.5"
      >
        <TwnMobileMenuToggle
          v-if="!isLargeDesktop"
          icon="menu"
        />
        <TwnBreadcrumbs
          :is-page-header="true"
          class="min-w-0"
        />
        <div
          :id="PAGE_HEADER_ACTIONS_ID"
          class="ml-auto flex flex-wrap items-center gap-2"
        />
        <TwnBaseBtn
          v-if="!isLargeDesktop"
          aria-label="Search"
          icon="search"
          severity="secondary"
          size="small"
          variant="outlined"
          @click="openCommandPalette"
        />
      </div>
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { router } from '@inertiajs/vue3'
import { computed, onMounted, onUnmounted, provide, ref } from 'vue'

import TwnBreadcrumbs from '@/components/atoms/breadcrumbs/twn-breadcrumbs/TwnBreadcrumbs.vue'
import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import { PAGE_HEADER_ACTIONS_TARGET } from '@/components/templates/scaffolds/entity-scaffold/EntityScaffold.consts'
import { useTailwindBreakpoints } from '@/composables/use-tailwind-breakpoints/useTailwindBreakpoints'
import TwnMobileMenuToggle from '@/domains/admin-panel/components/molecules/menu/twn-mobile-menu-toggle/TwnMobileMenuToggle.vue'
import { useCommandPalette } from '@/domains/command-center/composables/use-command-palette/useCommandPalette'

const { isLargeDesktop } = useTailwindBreakpoints()
const { open: openCommandPalette } = useCommandPalette()

const PAGE_HEADER_ACTIONS_ID = 'page-header-actions'
provide(
  PAGE_HEADER_ACTIONS_TARGET,
  computed(() => `#${PAGE_HEADER_ACTIONS_ID}`)
)

// Reference to the content area for scrolling
const mainContentAreaRef = ref<HTMLElement | null>(null)

// Reset scroll position when navigation is successful
const resetScroll = () => {
  if (mainContentAreaRef.value) {
    mainContentAreaRef.value.scrollTop = 0
  }
}

onMounted(() => {
  const removeEventListener = router.on('success', resetScroll)
  onUnmounted(removeEventListener)
})
</script>
